import * as Sentry from '@sentry/browser'

type GetLoggerOptions = {
  isVerboseDebug?: boolean
}

type Logger = {
  debugging: typeof console.log
  info: typeof console.log
  warn: typeof console.warn
  error: typeof console.error
}

type LogHistory = {
  scopeName: string
  level: 'debug' | 'info' | 'warn' | 'error'
  timestamp: string
  message: string
  args: unknown[]
  service: string
  trace_id?: string
}

const SERVICE_NAME = 'aurora-react'

function getTraceContext(): { trace_id?: string; span_id?: string } {
  try {
    const activeSpan = Sentry.getActiveSpan()
    if (!activeSpan) return {}
    const spanJson = Sentry.spanToJSON(activeSpan)
    return {
      trace_id: spanJson.trace_id,
      span_id: spanJson.span_id,
    }
  } catch {
    return {}
  }
}

const logHistory: LogHistory[] = []
const LOG_HISTORY_MAX_SIZE = 1000
function logToHistory(args: Omit<LogHistory, 'timestamp'>): void {
  logHistory.push({
    ...args,
    timestamp: getTimestamp(),
  })

  // Remove oldest log if history is too long
  if (logHistory.length > LOG_HISTORY_MAX_SIZE) {
    logHistory.shift()
  }
}

type GetLogHistoryOptions = {
  newestFirst?: boolean
}

export function getLogHistory({ newestFirst }: GetLogHistoryOptions = {}): LogHistory[] {
  if (newestFirst) {
    return [...logHistory].reverse()
  }

  return logHistory
}

export function clearLogHistory(): void {
  logHistory.length = 0
}

export function printLogHistory(): void {
  // biome-ignore lint/suspicious/noConsole: Ignore lint rule
  console.table(logHistory)
}

function getTimestamp(): string {
  return new Date().toISOString()
}

let isVerboseDebug = true
export function setVerboseDebug(enabled: boolean): void {
  isVerboseDebug = enabled
}

export default function getLogger(scopeName: string, options: GetLoggerOptions = {}): Logger {
  return {
    debugging: (message: string, ...args: unknown[]) => {
      const traceContext = getTraceContext()
      logToHistory({
        scopeName,
        level: 'debug',
        message,
        args,
        service: SERVICE_NAME,
        trace_id: traceContext.trace_id,
      })

      if (!isVerboseDebug) {
        return
      }

      if (options.isVerboseDebug === false) {
        return
      }

      // biome-ignore lint/suspicious/noConsole: Ignore lint rule
      console.log(`[${scopeName}] ${getTimestamp()} DEBUG: ${message}`, ...args)
    },
    info: (message: string, ...args: unknown[]) => {
      const traceContext = getTraceContext()
      logToHistory({
        scopeName,
        level: 'info',
        message,
        args,
        service: SERVICE_NAME,
        trace_id: traceContext.trace_id,
      })

      // biome-ignore lint/suspicious/noConsole: Ignore lint rule
      console.log(`[${scopeName}] ${getTimestamp()} ${message}`, ...args)
    },
    warn: (message: string, ...args: unknown[]) => {
      const traceContext = getTraceContext()
      logToHistory({
        scopeName,
        level: 'warn',
        message,
        args,
        service: SERVICE_NAME,
        trace_id: traceContext.trace_id,
      })

      Sentry.addBreadcrumb({
        category: 'log',
        message: `[${scopeName}] ${message}`,
        level: 'warning',
        data: {
          scopeName,
          service: SERVICE_NAME,
          ...traceContext,
        },
      })

      console.warn(`[${scopeName}] ${getTimestamp()} ${message}`, ...args)
    },
    error: (message: string, ...args: unknown[]) => {
      const traceContext = getTraceContext()
      logToHistory({
        scopeName,
        level: 'error',
        message,
        args,
        service: SERVICE_NAME,
        trace_id: traceContext.trace_id,
      })

      Sentry.addBreadcrumb({
        category: 'log',
        message: `[${scopeName}] ${message}`,
        level: 'error',
        data: {
          scopeName,
          service: SERVICE_NAME,
          ...traceContext,
        },
      })

      console.error(`[${scopeName}] ${getTimestamp()} ${message}`, ...args)
    },
  }
}
